import React, { useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { designToPreviewSafe, designFromPreviewSafe, fromPreviewSafe } from '../library/previewHostImages';

/**
 * Thin wrapper around `window.unlayer.createEditor(...)` that intentionally
 * bypasses the `react-email-editor` package.
 *
 * Why bypass: the bundled `react-email-editor@1.6.1` spreads user options
 * into `unlayer.createEditor` and then overwrites `displayMode: 'email'`
 * after the spread (see node_modules/react-email-editor/es/index.js around
 * line 44). That silently ignores any `displayMode: 'web'` passed via the
 * `options` prop, which is exactly the mode the storefront/admin editors
 * need to avoid the email-mode wrapper tables and the 100vh min-height that
 * make rendered HTML look indented on the live site.
 *
 * The ref API is intentionally compatible with the react-email-editor
 * component — `ref.current.editor` returns the unlayer instance — so call
 * sites can swap between the two without touching their loadDesign /
 * saveDesign / exportHtml usage.
 */

const UNLAYER_SCRIPT_URL = 'https://editor.unlayer.com/embed.js?2';

let scriptInjected = false;
const pendingCallbacks = [];

const flushPending = () => {
    while (pendingCallbacks.length) {
        const cb = pendingCallbacks.shift();
        try { cb(); } catch { /* swallow — one bad consumer must not block others */ }
    }
};

const ensureUnlayer = (cb) => {
    if (typeof window === 'undefined') return;
    if (window.unlayer && typeof window.unlayer.createEditor === 'function') {
        cb();
        return;
    }
    pendingCallbacks.push(cb);

    // Another instance (or react-email-editor on a different screen) may
    // have already injected the script. Re-use it if so.
    const existing = Array.from(document.querySelectorAll('script'))
        .find((s) => s.src && s.src.indexOf('editor.unlayer.com/embed.js') !== -1);

    if (existing) {
        // The script tag exists but `window.unlayer` is not ready yet — wait
        // for that script's load event. A short poll covers the case where
        // load has already fired but the global hasn't materialised yet.
        const poll = () => {
            if (window.unlayer && typeof window.unlayer.createEditor === 'function') {
                flushPending();
            } else {
                setTimeout(poll, 50);
            }
        };
        existing.addEventListener('load', flushPending);
        poll();
        return;
    }

    if (scriptInjected) return; // injection in flight from this module already
    scriptInjected = true;
    const tag = document.createElement('script');
    tag.src = UNLAYER_SCRIPT_URL;
    tag.onload = flushPending;
    document.head.appendChild(tag);
};

let editorIdCounter = 0;

/**
 * Body of an empty web-mode design, as the legacy admin started every content
 * editor (content-editor.component.ts: loadBlank on init and for a language
 * without a design): transparent background, so the storefront's own colour
 * shows (Unlayer's default is grey #e7e7e7), full width and no forced height.
 * `blankOptions` overrides per editor (dynamic pages: 1200px wide). Email-mode
 * editors (voucher designer) keep Unlayer's defaults, as they did in legacy.
 */
const WEB_BLANK_DEFAULTS = {
    backgroundColor: 'TRANSPARENT',
    contentWidth:    '100%',
    minHeight:       'auto',
};

/**
 * Web-mode exports wrap the page in `<div id="u_body" style="min-height: 100vh">`,
 * so every saved page is at least one full screen tall -- a short text leaves a
 * huge blank area on the storefront. The legacy admin rewrote that on every
 * export (content-editor.component.ts); this is the same replacement, verbatim,
 * so content saved here renders exactly like content saved there.
 * Email-mode editors (voucher designer) are left alone, as they were in legacy.
 */
const normalizeWebExport = (data) => {
    try {
        if (data && typeof data.html === 'string') {
            data.html = data.html.replace('min-height: 100vh;', 'min-height: auto;margin-bottom: 42px;');
        }
    } catch { /* leave the export untouched */ }
    return data;
};

const UnlayerEditor = forwardRef(function UnlayerEditor(
    { minHeight = '500px', displayMode = 'web', options, appearance, blankOptions, onReady, style },
    ref,
) {
    const editorRef    = useRef(null);
    const containerId  = useRef(null);

    if (containerId.current === null) {
        editorIdCounter += 1;
        containerId.current = `unlayer-editor-${editorIdCounter}`;
    }

    useImperativeHandle(ref, () => ({
        get editor() { return editorRef.current; },
    }), []);

    useEffect(() => {
        // StrictMode runs effects twice in dev; skip if we already have an
        // editor attached to avoid double-mounting unlayer iframes.
        if (editorRef.current) return undefined;

        let cancelled = false;
        ensureUnlayer(() => {
            if (cancelled || editorRef.current) return;
            if (!window.unlayer || typeof window.unlayer.createEditor !== 'function') return;

            // Caller-supplied options win over our defaults, but our defaults
            // win over the hardcoded behaviour of unlayer's internal embed
            // bundle — order: defaults < appearance < user options.
            const created = window.unlayer.createEditor({
                id:          containerId.current,
                displayMode,
                ...(appearance ? { appearance } : {}),
                ...(options || {}),
                // Make sure the id always points at our container even if a
                // consumer passes `options.id` by mistake.
                ...{ id: containerId.current },
            });
            // Every host reads and writes the page through loadDesign /
            // saveDesign / exportHtml, so patching them on the instance covers
            // all of them (and any future one) in one place.
            const effectiveMode = (options && options.displayMode) || displayMode;
            if (created && !created.__sftExportPatched) {
                // Images on a hoster preview host (self-signed cert) are shown
                // through auth's passthrough while editing; the original URL is
                // restored on every way out, so nothing saved ever contains it.
                if (typeof created.loadDesign === 'function') {
                    const originalLoad = created.loadDesign.bind(created);
                    created.loadDesign = (design, ...rest) => originalLoad(designToPreviewSafe(design), ...rest);
                }
                if (typeof created.saveDesign === 'function') {
                    const originalSave = created.saveDesign.bind(created);
                    created.saveDesign = (callback, ...rest) => originalSave(
                        (design) => callback(designFromPreviewSafe(design)),
                        ...rest,
                    );
                }
                if (typeof created.exportHtml === 'function') {
                    const originalExport = created.exportHtml.bind(created);
                    created.exportHtml = (callback, exportOptions) => originalExport(
                        (data) => {
                            let out = data;
                            if (out && typeof out === 'object') {
                                out = { ...out };
                                if (typeof out.html === 'string') out.html = fromPreviewSafe(out.html);
                                if (out.design) out.design = designFromPreviewSafe(out.design);
                            }
                            callback(effectiveMode !== 'email' ? normalizeWebExport(out) : out);
                        },
                        exportOptions,
                    );
                }
                // A blank web page starts like it did in the legacy admin; a
                // caller's own loadBlank({...}) still wins key by key.
                if (effectiveMode !== 'email' && typeof created.loadBlank === 'function') {
                    const originalBlank = created.loadBlank.bind(created);
                    const blankBody = { ...WEB_BLANK_DEFAULTS, ...(blankOptions || {}) };
                    created.loadBlank = (opts, ...rest) => originalBlank({ ...blankBody, ...(opts || {}) }, ...rest);
                    // Unlayer opens on its own (grey) blank until something is
                    // loaded; hosts only load a design when there is one.
                    try { created.loadBlank(); } catch { /* the host loads a design next */ }
                }
                created.__sftExportPatched = true;
            }
            editorRef.current = created;
            if (typeof onReady === 'function') {
                try { onReady(created); } catch { /* ignore consumer errors */ }
            }
        });

        return () => {
            cancelled = true;
            // The embed bundle has no public destroy API; clearing the ref
            // is enough — React will unmount the container div and the
            // iframe goes with it.
            editorRef.current = null;
        };
        // Mount-once: re-running this effect would tear down and rebuild the
        // editor on every render, destroying any in-progress edits. Updates
        // to options/appearance after mount have to be applied via the ref
        // (e.g. `editorRef.current.loadDesign(...)`).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Mirror react-email-editor's wrapper structure: two nested flex
    // containers with `flex:1, display:flex, minHeight: <h>`. Unlayer's
    // iframe sizes itself off the flex item it's mounted into, so a plain
    // `minHeight` on a single div leaves it collapsed inside the wrapper.
    const minH = typeof minHeight === 'number' ? `${minHeight}px` : minHeight;
    const outerStyle = { flex: 1, display: 'flex', minHeight: minH, ...(style || {}) };
    const innerStyle = { flex: 1, display: 'flex', minHeight: minH };

    return (
        <div style={outerStyle}>
            <div id={containerId.current} style={innerStyle} />
        </div>
    );
});

UnlayerEditor.propTypes = {
    minHeight:   PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    displayMode: PropTypes.oneOf(['web', 'email']),
    options:     PropTypes.object,
    appearance:  PropTypes.object,
    blankOptions: PropTypes.object,
    onReady:     PropTypes.func,
    style:       PropTypes.object,
};

export default UnlayerEditor;
